// src/hooks/useCalendarNavigation.js - COMPLETE Calendar State Management Hook
import { useState, useCallback, useMemo, useEffect } from 'react';

/**
 * Complete calendar state management hook
 * 
 * This hook manages ALL calendar state:
 * - Navigation (view, date, title)
 * - Filters (status, category, customer)
 * - URL synchronization
 * - Data loading ranges
 * - Keyboard navigation
 * 
 * @param {Object} options - Configuration options
 * @returns {Object} Complete calendar state and functions
 */
export const useCalendarNavigation = (options = {}) => {
  const {
    initialView = 'timeGridWeek',
    initialDate = new Date(),
    updateURL = true, // Enable URL sync by default
    location = null // React Router location for initial state
  } = options;

  // ===============================================
  // CORE NAVIGATION STATE
  // ===============================================
  
  const [currentView, setCurrentView] = useState(initialView);
  const [currentDate, setCurrentDate] = useState(initialDate);

  // ===============================================
  // FILTER STATE (from old useCalendarState)
  // ===============================================
  
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  // ===============================================
  // INITIALIZATION FROM URL/LOCATION
  // ===============================================
  
  // Extract initial state from location (React Router)
  useEffect(() => {
    if (location?.state) {
      if (location.state.initialFilter) {
        setStatusFilter(location.state.initialFilter);
      }
      if (location.state.initialView) {
        setCurrentView(location.state.initialView);
      }
    }
    
    // Extract from URL params
    if (updateURL && typeof window !== 'undefined') {
      const url = new URL(window.location);
      const urlView = url.searchParams.get('view');
      const urlDate = url.searchParams.get('date');
      
      if (urlView && ['timeGridWeek', 'dayGridMonth', 'timeGridDay'].includes(urlView)) {
        setCurrentView(urlView);
      }
      
      if (urlDate) {
        const parsedDate = new Date(urlDate);
        if (!isNaN(parsedDate.getTime())) {
          setCurrentDate(parsedDate);
        }
      }
    }
  }, [location, updateURL]);

  // ===============================================
  // NAVIGATION FUNCTIONS
  // ===============================================

  // View change handler with URL sync
  const handleViewChange = useCallback((newView) => {
    console.log('🔄 Calendar Navigation: View changed to', newView);
    setCurrentView(newView);
    
    // Update URL
    if (updateURL && typeof window !== 'undefined') {
      const url = new URL(window.location);
      url.searchParams.set('view', newView);
      url.searchParams.set('date', currentDate.toISOString().split('T')[0]);
      window.history.replaceState({}, '', url);
    }
  }, [updateURL, currentDate]);

  // Date navigation handlers
  const navigatePrevious = useCallback(() => {
    const newDate = new Date(currentDate);
    
    switch (currentView) {
      case 'dayGridMonth':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'timeGridWeek':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'timeGridDay':
        newDate.setDate(newDate.getDate() - 1);
        break;
    }
    
    console.log('🔄 Calendar Navigation: Navigate previous to', newDate.toLocaleDateString());
    setCurrentDate(newDate);
    
    // Update URL
    if (updateURL && typeof window !== 'undefined') {
      const url = new URL(window.location);
      url.searchParams.set('date', newDate.toISOString().split('T')[0]);
      window.history.replaceState({}, '', url);
    }
  }, [currentDate, currentView, updateURL]);

  const navigateNext = useCallback(() => {
    const newDate = new Date(currentDate);
    
    switch (currentView) {
      case 'dayGridMonth':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'timeGridWeek':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'timeGridDay':
        newDate.setDate(newDate.getDate() + 1);
        break;
    }
    
    console.log('🔄 Calendar Navigation: Navigate next to', newDate.toLocaleDateString());
    setCurrentDate(newDate);
    
    // Update URL
    if (updateURL && typeof window !== 'undefined') {
      const url = new URL(window.location);
      url.searchParams.set('date', newDate.toISOString().split('T')[0]);
      window.history.replaceState({}, '', url);
    }
  }, [currentDate, currentView, updateURL]);

  const navigateToToday = useCallback(() => {
    const today = new Date();
    console.log('🔄 Calendar Navigation: Navigate to today');
    setCurrentDate(today);
    
    // Update URL
    if (updateURL && typeof window !== 'undefined') {
      const url = new URL(window.location);
      url.searchParams.set('date', today.toISOString().split('T')[0]);
      window.history.replaceState({}, '', url);
    }
  }, [updateURL]);

  // Navigate to specific date
  const navigateToDate = useCallback((date) => {
    const newDate = new Date(date);
    console.log('🔄 Calendar Navigation: Navigate to specific date', newDate.toLocaleDateString());
    setCurrentDate(newDate);
    
    // Update URL
    if (updateURL && typeof window !== 'undefined') {
      const url = new URL(window.location);
      url.searchParams.set('date', newDate.toISOString().split('T')[0]);
      window.history.replaceState({}, '', url);
    }
  }, [updateURL]);

  // ===============================================
  // FILTER FUNCTIONS (from old useCalendarState)
  // ===============================================

  const clearFilters = useCallback(() => {
    console.log('🔄 Calendar Navigation: Clearing all filters');
    setStatusFilter('');
    setCategoryFilter('');
    setCustomerFilter('');
  }, []);

  const updateFilters = useCallback((filters) => {
    console.log('🔄 Calendar Navigation: Updating filters', filters);
    if (filters.statusFilter !== undefined) setStatusFilter(filters.statusFilter);
    if (filters.categoryFilter !== undefined) setCategoryFilter(filters.categoryFilter);
    if (filters.customerFilter !== undefined) setCustomerFilter(filters.customerFilter);
  }, []);

  // ===============================================
  // COMPUTED VALUES
  // ===============================================

  // Generate title based on current view and date
  const currentTitle = useMemo(() => {
    const formatOptions = {
      month: 'long',
      year: 'numeric'
    };

    switch (currentView) {
      case 'dayGridMonth':
        return currentDate.toLocaleDateString('en-US', formatOptions);
        
      case 'timeGridWeek': {
        // Get start and end of week
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        // If same month, show "Month Year"
        if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
          return startOfWeek.toLocaleDateString('en-US', formatOptions);
        } else {
          // Different months, show range
          return `${startOfWeek.toLocaleDateString('en-US', { month: 'short' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
        }
      }
        
      case 'timeGridDay':
        return currentDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
        
      default:
        return currentDate.toLocaleDateString('en-US', formatOptions);
    }
  }, [currentDate, currentView]);

  // Helper to get date ranges for data loading
  const getDateRange = useCallback(() => {
    switch (currentView) {
      case 'dayGridMonth': {
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        return {
          start: startOfMonth,
          end: endOfMonth
        };
      }
        
      case 'timeGridWeek': {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          start: startOfWeek,
          end: endOfWeek
        };
      }
        
      case 'timeGridDay': {
        const dayStart = new Date(currentDate);
        const dayEnd = new Date(currentDate);
        return {
          start: dayStart,
          end: dayEnd
        };
      }
        
      default:
        return {
          start: new Date(currentDate),
          end: new Date(currentDate)
        };
    }
  }, [currentDate, currentView]);

  // Check if current date is today
  const isToday = useMemo(() => {
    const today = new Date();
    return currentDate.toDateString() === today.toDateString();
  }, [currentDate]);

  // Check if we're viewing current period (month/week containing today)
  const isCurrentPeriod = useMemo(() => {
    const today = new Date();
    
    switch (currentView) {
      case 'dayGridMonth':
        return currentDate.getMonth() === today.getMonth() && 
               currentDate.getFullYear() === today.getFullYear();
               
      case 'timeGridWeek': {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return today >= startOfWeek && today <= endOfWeek;
      }
        
      case 'timeGridDay':
        return isToday;
        
      default:
        return false;
    }
  }, [currentDate, currentView, isToday]);

  // ===============================================
  // KEYBOARD NAVIGATION
  // ===============================================

  // Keyboard navigation support
  const handleKeyboardNavigation = useCallback((event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        navigatePrevious();
        break;
      case 'ArrowRight':
        event.preventDefault();
        navigateNext();
        break;
      case 'Home':
        event.preventDefault();
        navigateToToday();
        break;
      case '1':
        event.preventDefault();
        handleViewChange('dayGridMonth');
        break;
      case '2':
        event.preventDefault();
        handleViewChange('timeGridWeek');
        break;
      case '3':
        event.preventDefault();
        handleViewChange('timeGridDay');
        break;
    }
  }, [navigatePrevious, navigateNext, navigateToToday, handleViewChange]);

  // ===============================================
  // LEGACY COMPATIBILITY (for migration)
  // ===============================================
  
  // Legacy methods from old useCalendarState (for backward compatibility during migration)
  const navigateCalendar = useCallback((direction) => {
    if (direction > 0) {
      navigateNext();
    } else {
      navigatePrevious();
    }
  }, [navigateNext, navigatePrevious]);

  const goToToday = navigateToToday;
  
  const getCalendarTitle = useCallback(() => currentTitle, [currentTitle]);

  // ===============================================
  // RETURN ALL STATE AND FUNCTIONS
  // ===============================================

  return {
    // ==========================================
    // CORE NAVIGATION STATE
    // ==========================================
    currentView,
    currentDate,
    currentTitle,
    
    // ==========================================
    // FILTER STATE
    // ==========================================
    statusFilter,
    categoryFilter, 
    customerFilter,
    filterDropdownOpen,
    
    // ==========================================
    // NAVIGATION FUNCTIONS
    // ==========================================
    setView: handleViewChange,
    navigatePrevious,
    navigateNext,
    navigateToToday,
    navigateToDate,
    
    // ==========================================
    // FILTER FUNCTIONS
    // ==========================================
    setStatusFilter,
    setCategoryFilter,
    setCustomerFilter,
    setFilterDropdownOpen,
    clearFilters,
    updateFilters,
    
    // ==========================================
    // UTILITIES
    // ==========================================
    getDateRange,
    isToday,
    isCurrentPeriod,
    
    // ==========================================
    // KEYBOARD SUPPORT
    // ==========================================
    handleKeyboardNavigation,
    
    // ==========================================
    // LEGACY COMPATIBILITY (temporary)
    // ==========================================
    navigateCalendar,
    goToToday,
    getCalendarTitle,
    setCurrentView, // Raw setter for edge cases
    setCurrentDate  // Raw setter for edge cases
  };
};